'use client';

import { createAdminPlace, updateAdminPlace } from '@/lib/actions/admin/placeActions';
import { useRouter } from 'next/navigation';
import React, { useEffect } from 'react';
import { useFormState } from 'react-dom';
import { Place } from './PlacesDataTable';

// Adjust Place interface based on actual data model for a Place
interface Place {
    id?: string; // Optional for new places
    name: string;
    address?: string;
    description?: string;
    // Add other fields as necessary
}

interface PlaceFormProps {
    place?: Place | null; // Existing place data for editing, null/undefined for new
    locale: string;
}

// Define a form state type
interface FormState {
    message: string;
    errors: Record<string, string[]>;
    success: boolean;
}

// Initial form state
const initialFormState: FormState = {
    message: '',
    errors: {},
    success: false
};

const PlaceForm: React.FC<PlaceFormProps> = ({ place, locale }) => {
    const router = useRouter();
    const isEditing = !!place;

    // Create a server action bound to the form state
    const createAction = async (prevState: FormState, formData: FormData) => {
        const placeData = {
            name: formData.get('name') as string,
            address: formData.get('address') as string,
            description: formData.get('description') as string,
            // Add other fields as needed
        };

        try {
            const result = await createAdminPlace(locale, placeData);

            if (result) {
                return {
                    message: 'Place created successfully!',
                    errors: {},
                    success: true
                };
            } else {
                return {
                    message: 'Failed to create place',
                    errors: {},
                    success: false
                };
            }
        } catch (error) {
            console.error('Create error:', error);
            return {
                message: 'Failed to create place',
                errors: {},
                success: false
            };
        }
    };

    // Update action
    const updateAction = async (prevState: FormState, formData: FormData) => {
        if (!place?.id) return prevState;

        const placeData = {
            name: formData.get('name') as string,
            address: formData.get('address') as string,
            description: formData.get('description') as string,
            // Add other fields as needed
        };

        try {
            const result = await updateAdminPlace(locale, place.id, placeData);

            if (result) {
                return {
                    message: 'Place updated successfully!',
                    errors: {},
                    success: true
                };
            } else {
                return {
                    message: 'Failed to update place',
                    errors: {},
                    success: false
                };
            }
        } catch (error) {
            console.error('Update error:', error);
            return {
                message: 'Failed to update place',
                errors: {},
                success: false
            };
        }
    };

    // Use the appropriate action based on whether we're editing or creating
    const [formState, formAction] = useFormState(
        isEditing ? updateAction : createAction,
        initialFormState
    );

    // When form submission is successful, redirect after a delay
    useEffect(() => {
        if (formState.success) {
            const timer = setTimeout(() => {
                router.push(`/${locale}/dashboard/places`);
            }, 1500);

            return () => clearTimeout(timer);
        }
    }, [formState.success, router, locale]);

    return (
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 max-w-3xl mx-auto">
            {formState.message && (
                <div className={`mb-4 p-4 rounded-md ${formState.success ? 'bg-green-100 text-green-700 border border-green-400' : 'bg-red-100 text-red-700 border border-red-400'}`}>
                    {formState.message}
                </div>
            )}

            <form action={formAction} className="space-y-6">
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="name"
                        id="name"
                        defaultValue={place?.name || ''}
                        required
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                    />
                    {formState.errors?.name && (
                        <p className="mt-1 text-sm text-red-600">{formState.errors.name.join(', ')}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Address
                    </label>
                    <input
                        type="text"
                        name="address"
                        id="address"
                        defaultValue={place?.address || ''}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                    />
                    {formState.errors?.address && (
                        <p className="mt-1 text-sm text-red-600">{formState.errors.address.join(', ')}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Description
                    </label>
                    <textarea
                        name="description"
                        id="description"
                        rows={4}
                        defaultValue={place?.description || ''}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                    />
                    {formState.errors?.description && (
                        <p className="mt-1 text-sm text-red-600">{formState.errors.description.join(', ')}</p>
                    )}
                </div>

                {/* Add other form fields here based on the Place model */}

                <div className="flex justify-between">
                    <button
                        type="button"
                        onClick={() => router.push(`/${locale}/dashboard/places`)}
                        className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        {isEditing ? 'Update Place' : 'Create Place'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PlaceForm; 